// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { shouldFetchNotifications } from './notificationsFetchUtils';

// Selector
import { isGuestSelector } from '../../../../user/currentUserSelector';
import {
    getNotificationsErrorTimestamp,
    getNotificationsFetchedTimestamp,
} from '../../../../notifications/notificationsSelector';
import {
    selectSocketInterruptionTime,
    selectSocketReconnectionMode,
    selectSocketConnectionTime,
    selectIsSocketConnected,
} from '../../../../utils/socket/socketConnectionSelector';

// Actions
import { fetchNotifications } from '../../../../notifications/notificationActions';

// Constants
import { TIMES } from '../../../../../common/utils/timeUtil';
import { SocketReconnectionMode } from '../../../../utils/socket/socketConstants';

const mapStateToProps = () =>
    createStructuredSelector({
        socketReconnectionMode: selectSocketReconnectionMode,
        notificationsFetchedTime: getNotificationsFetchedTimestamp,
        notificationsErrorTime: getNotificationsErrorTimestamp,
        socketConnectionTime: selectSocketConnectionTime,
        socketInterruptionTime: selectSocketInterruptionTime,
        isSocketConnected: selectIsSocketConnected,
        isGuest: isGuestSelector,
    });

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchNotifications: (args) => dispatch(fetchNotifications(args)),
});

/**
 * Fetches the notifications again if the user has been disconnected from the sockets.
 *
 * NOTE: I feel like it might be more appropriate for this logic to be in middleware as
 *  opposed to a component. As it should really be responding to a SOCKET_CONNECTION
 *  action, rather than a component lifecycle event.
 */
export default (DecoratedComponent) => {
    @connect(mapStateToProps, mapDispatchToProps)
    class notificationsFetchDecorator extends React.Component {
        constructor(props) {
            super(props);
            this.fetchTimeoutId = null;
        }

        componentDidUpdate(prevProps) {
            if (!shouldFetchNotifications(this.props)) return;

            if (this.fetchTimeoutId) clearTimeout(this.fetchTimeoutId);

            const timeDelay =
                // If the fetch is occurring due to lost connection then randomly delay the fetching of
                // notifications so we don't overwhelm the server
                this.props.socketReconnectionMode === SocketReconnectionMode.CONNECTION_LOST
                    ? Math.random() * 10 * TIMES.MINUTE
                    : 0;

            // Fetch sometime in the next 10 mins to ease load on servers after a WS server is stopped
            this.fetchTimeoutId = setTimeout(() => {
                this.fetchNotifications();
            }, timeDelay);
        }

        fetchNotifications = () => {
            const { isGuest, dispatchFetchNotifications, notificationsFetchedTime } = this.props;

            if (isGuest) return;

            dispatchFetchNotifications({ after: notificationsFetchedTime });
        };

        render() {
            return <DecoratedComponent {...this.props} />;
        }
    }

    notificationsFetchDecorator.propTypes = {
        isGuest: PropTypes.bool,
        isSocketConnected: PropTypes.bool,
        notificationsFetchedTime: PropTypes.number,
        notificationsErrorTime: PropTypes.number,
        dispatchFetchNotifications: PropTypes.func,
        socketReconnectionMode: PropTypes.string,
        socketConnectionTime: PropTypes.number,
        socketInterruptionTime: PropTypes.number,
    };

    return notificationsFetchDecorator;
};
